import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useAuth } from '../contexts/AuthContext'
import { useCurrency } from '../contexts/CurrencyContext'
import { analyticsService, type ProjectedIncomeAnalysis } from '../services/analyticsService'
import { 
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon,
  SparklesIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline'

interface RevenueAnalyticsProps {
  refreshTrigger?: number
  onDataLoaded?: (data: ProjectedIncomeAnalysis) => void
}

const RevenueAnalytics = ({ refreshTrigger, onDataLoaded }: RevenueAnalyticsProps) => {
  const { user } = useAuth()
  const { selectedCurrency } = useCurrency()
  const [analysis, setAnalysis] = useState<ProjectedIncomeAnalysis | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (user) {
      loadAnalysis()
    }
  }, [user, refreshTrigger])

  const loadAnalysis = async () => {
    if (!user) return
    
    console.log('RevenueAnalytics: Loading analysis for user', user.id, 'refreshTrigger:', refreshTrigger)
    setLoading(true)
    try {
      const projectedAnalysis = await analyticsService.calculateProjectedAnnualIncome(user.id)
      console.log('RevenueAnalytics: Analysis loaded:', projectedAnalysis)
      setAnalysis(projectedAnalysis)
      onDataLoaded?.(projectedAnalysis)
    } catch (error) {
      console.error('Error loading revenue analysis:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: selectedCurrency.code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'increasing': return ArrowTrendingUpIcon
      case 'decreasing': return ArrowTrendingDownIcon
      default: return MinusIcon
    }
  }

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'increasing': return 'text-green-400'
      case 'decreasing': return 'text-red-400'
      default: return 'text-gray-400'
    }
  }

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-gray-400">Analyzing revenue trends...</p>
      </div>
    )
  }

  if (!analysis) {
    return (
      <div className="text-center py-8">
        <ExclamationTriangleIcon className="w-16 h-16 text-gray-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-white mb-2">Unable to load revenue analysis</h3>
        <p className="text-gray-400">Please try again later.</p>
      </div>
    )
  }

  const TrendIcon = getTrendIcon(analysis.trend)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 flex items-center justify-center">
          <span className="text-2xl">💰</span>
        </div>
        <h3 className="text-xl font-bold text-white mb-2">Revenue Analytics</h3>
        <p className="text-gray-400">
          AI-powered income analysis and projections based on your invoicing patterns
        </p>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Current Month */}
        <div className="glass-mockup rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-white font-semibold">This Month</h4>
            <ChartBarIcon className="w-5 h-5 text-blue-400 flex-shrink-0" />
          </div>
          <div className="text-2xl sm:text-3xl font-bold text-white mb-2">
            {formatCurrency(analysis.currentMonthIncome)}
          </div>
          <p className="text-gray-400 text-sm">
            Based on {analysis.monthsOfData} months of data
          </p>
        </div>

        {/* Projected Annual */}
        <div className="glass-mockup rounded-xl p-6 border-2 border-purple-500/30 bg-purple-500/10">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-white font-semibold flex items-center space-x-2">
              <SparklesIcon className="w-4 h-4 text-purple-400 flex-shrink-0" />
              <span className="text-sm sm:text-base">AI Projected Annual</span>
            </h4>
            <div className="flex items-center space-x-1 flex-shrink-0">
              <TrendIcon className={`w-4 h-4 ${getTrendColor(analysis.trend)}`} />
              <span className={`text-xs ${getTrendColor(analysis.trend)}`}>
                {analysis.trendPercentage !== 0 && `${analysis.trendPercentage > 0 ? '+' : ''}${analysis.trendPercentage}%`}
              </span>
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-bold text-white mb-2">
            {formatCurrency(analysis.projectedAnnualIncome)}
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <p className="text-purple-400 text-sm">
              {analysis.confidence}% confidence
            </p>
            <span className={`px-2 py-1 rounded-full text-xs font-medium self-start sm:self-auto ${
              analysis.trend === 'increasing' 
                ? 'bg-green-500/20 text-green-400'
                : analysis.trend === 'decreasing'
                ? 'bg-red-500/20 text-red-400'
                : 'bg-gray-500/20 text-gray-400'
            }`}>
              {analysis.trend === 'increasing' ? 'Growing' : 
               analysis.trend === 'decreasing' ? 'Declining' : 'Stable'}
            </span>
          </div>
        </div>
      </div>

      {/* AI Recommendation */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl border border-purple-500/30"
      >
        <div className="flex items-start space-x-3">
          <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
            <SparklesIcon className="w-4 h-4 text-purple-400" />
          </div>
          <div>
            <h5 className="text-white font-medium mb-1">AI Recommendation</h5>
            <p className="text-purple-300 text-sm">{analysis.recommendation}</p>
          </div>
        </div>
      </motion.div>

      {/* Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="glass-mockup rounded-xl p-4 text-center">
          <div className="text-lg font-bold text-white mb-1">
            {formatCurrency(analysis.projectedAnnualIncome / 12)}
          </div>
          <div className="text-gray-400 text-sm">Avg Monthly</div>
        </div>
        
        <div className="glass-mockup rounded-xl p-4 text-center">
          <div className="text-lg font-bold text-white mb-1">
            {formatCurrency(analysis.projectedAnnualIncome / 52)}
          </div>
          <div className="text-gray-400 text-sm">Avg Weekly</div>
        </div>
        
        <div className="glass-mockup rounded-xl p-4 text-center">
          <div className="text-lg font-bold text-white mb-1">
            {analysis.monthsOfData}
          </div>
          <div className="text-gray-400 text-sm">Months of Data</div>
        </div>
      </div>

      {/* Tips */}
      <div className="p-4 bg-blue-500/10 rounded-xl border border-blue-500/30">
        <div className="flex items-start space-x-3">
          <LightBulbIcon className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h5 className="text-blue-400 font-medium mb-2">💡 Tips to Improve Accuracy</h5>
            <ul className="text-blue-300 text-sm space-y-1">
              <li>• Create and send invoices regularly to build more data</li>
              <li>• Mark invoices as "paid" when payment is received</li>
              <li>• The AI becomes more accurate with 6+ months of consistent data</li>
              {analysis.monthsOfData < 6 && (
                <li>• You need {6 - analysis.monthsOfData} more months for optimal projections</li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RevenueAnalytics 